import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { AuthShell, GoogleButton } from "./Login";
import { Loader2 } from "lucide-react";

// Public signup is CUSTOMER-ONLY. Crew accounts are invite-only (admin issues
// a code via /ops/crew → the invitee accepts it at /join?code=...).
export default function Register() {
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", referral_code: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      navigate("/portal");
    } catch (err) {
      setError(errMsg(err));
    }
    setLoading(false);
  };

  return (
    <AuthShell title="Create your customer account" subtitle="Book services, track jobs, pay invoices.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" data-testid="register-name-input" value={form.name} onChange={set("name")} placeholder="Jordan Smith" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="reg-email">Email</Label>
          <Input id="reg-email" name="email" autoComplete="email" data-testid="register-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" data-testid="register-phone-input" value={form.phone} onChange={set("phone")} placeholder="(619) 555-0100" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ref-code">Referral code</Label>
            <Input id="ref-code" data-testid="register-referral-input" value={form.referral_code} onChange={set("referral_code")} placeholder="CC-XXXXXX" className="uppercase" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="reg-password">Password</Label>
          <Input id="reg-password" name="new-password" autoComplete="new-password" data-testid="register-password-input" type="password" value={form.password} onChange={set("password")} placeholder="Min 6 characters" required />
        </div>
        {error && <p data-testid="register-error-message" className="text-sm text-red-500">{error}</p>}
        <Button data-testid="register-submit-button" type="submit" disabled={loading} className="w-full rounded-full h-11 btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Customer Account"}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">OR</span><div className="flex-1 h-px bg-border" />
      </div>
      <GoogleButton onClick={googleLogin} />
      <p className="text-sm text-muted-foreground mt-8 text-center">
        Already registered? <Link to="/login" data-testid="register-login-link" className="text-[var(--cc-accent)] font-semibold">Sign in</Link>
      </p>
      <p className="text-[11px] text-muted-foreground mt-3 text-center">
        Joining the crew? Crew accounts are invite-only — ask an admin for a code.
      </p>
    </AuthShell>
  );
}
